import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { COMPANY } from "../../lib/content";
import { Footer } from "./Footer";

const renderInline = (text) => {
  const nodes = [];
  const regex = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g;
  let last = 0;
  let m;
  let key = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith("**")) {
      nodes.push(
        <strong key={key++} className="text-[#FDFBF7] font-semibold">
          {tok.slice(2, -2)}
        </strong>
      );
    } else {
      const t = tok.match(/\[([^\]]+)\]\(([^)]+)\)/);
      nodes.push(
        <Link key={key++} to={t[2]} className="text-[#D4AF37] underline underline-offset-2 hover:text-[#F0C742]">
          {t[1]}
        </Link>
      );
    }
    last = m.index + tok.length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
};

const renderContent = (content) => {
  const lines = content.split("\n");
  const blocks = [];
  let list = [];
  const flush = () => {
    if (list.length) {
      blocks.push(
        <ul key={`ul-${blocks.length}`} className="list-disc pl-6 space-y-2 my-4 marker:text-[#D4AF37]">
          {list.map((li, i) => (
            <li key={i}>{renderInline(li)}</li>
          ))}
        </ul>
      );
      list = [];
    }
  };
  lines.forEach((raw) => {
    const line = raw.trim();
    if (!line) {
      flush();
      return;
    }
    if (line.startsWith("- ")) {
      list.push(line.slice(2));
    } else {
      flush();
      blocks.push(
        <p key={`p-${blocks.length}`} className="my-3 leading-relaxed whitespace-pre-line">
          {renderInline(line)}
        </p>
      );
    }
  });
  flush();
  return blocks;
};

export const LegalPage = ({ doc, testid }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [doc]);

  return (
    <>
      <header className="border-b border-[#D4AF37]/15 bg-[#0A110D]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <Link to="/" data-testid="legal-logo-home" className="flex items-center">
            <img src={COMPANY.logoWordmark} alt="North Emerald Holdings LLC" className="h-10 md:h-11 w-auto object-contain" />
          </Link>
          <Link
            to="/"
            data-testid="legal-back-home"
            className="inline-flex items-center gap-2 text-sm font-medium text-[#A3B1A8] hover:text-[#D4AF37] transition-colors"
          >
            <ArrowLeft size={16} /> Back to Home
          </Link>
        </div>
      </header>

      <main data-testid={testid} className="bg-[#0A110D] min-h-screen">
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <span className="overline">Legal</span>
            <h1 className="font-serif-display text-[#FDFBF7] text-4xl md:text-5xl tracking-tight mt-4">
              {doc.title}
            </h1>
            <div className="mt-4 text-sm text-[#A3B1A8]">
              <span>Effective Date: {doc.effectiveDate}</span>
              <span className="mx-2">·</span>
              <span>Last Updated: {doc.lastUpdated}</span>
            </div>

            <p className="mt-8 text-[#DBE4DE] text-base md:text-lg leading-relaxed">{doc.intro}</p>

            <div className="mt-12 space-y-10">
              {doc.sections.map((s) => (
                <section key={s.title} data-testid={`legal-section-${s.title.split(".")[0].trim()}`}>
                  <h2 className="font-serif-display text-[#FDFBF7] text-2xl mb-2">{s.title}</h2>
                  <div className="text-[#A3B1A8] text-base">{renderContent(s.content)}</div>
                </section>
              ))}
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </>
  );
};
